<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Examples/Generic_Types';
    let nextLink= '/Examples/Generic_Types/The_Typer_Definition';
    
</script>
<PrevNextSection {prevLink} {nextLink} />
<SectionComponent tag="h1" 
  id="the-metamodel-definition-of-the-example-language-1"
  bind:intersecting={visibleById["the-metamodel-definition-of-the-example-language-1"]}
  onIntersectChange={(visible) => setVisible("the-metamodel-definition-of-the-example-language-1", visible)}
>The Metamodel Definition of the Example Language</SectionComponent>
<p>To explain the type definition, we first need the <strong>metamodel</strong> of the example language.
Below is the complete <code>.ast</code> file—first, a few highlights.</p>
<p>Every type in the list of <em>expression – declared type</em> pairs is an instance of the AST
concept <strong>TypeUsage</strong>, which comes in two flavours: a <strong>type reference</strong> (pointing to a named type),
and a <strong>type declaration</strong> (an inline declaration that stands on its own).</p>
<pre class="language-freon"><code class="language-freon">// TyperExample/src/defs/projectY.ast#L18-L26

abstract concept TypeUsage &#123; // to be used wherever a type can be used, either a referred type of or declared type is correct
&#125;

concept TypeRef base TypeUsage  &#123;
    reference type: NamedType;
&#125;

abstract concept TypeDecl base TypeUsage &#123;
&#125;</code></pre>
<p>There are two options for a type declaration: a <strong>generic type</strong> and a <strong>unit of measurement</strong>.
For this example we borrow the generic kinds from OCL: <code>Set</code>, <code>Bag</code>, <code>Sequence</code>, and <code>Collection</code>.
Note that these are <strong>AST declarations</strong>; they are not type concepts by themselves.</p>
<pre class="language-freon"><code class="language-freon">// TyperExample/src/defs/projectY.ast#L37-L47

concept GenericType base TypeDecl &#123;
    baseType: TypeUsage;
    kind: GenericKind; // is it a set, sequence, bag, or anything else
&#125;
limited GenericKind &#123; Set; Sequence; Bag; Collection; &#125;

concept UnitOfMeasurement base TypeDecl &#123;
    reference baseType: PredefinedType; // is always NUMBER!!
    unit: UnitKind; // is it measured in km, kWh, grams, or anything else
&#125;
limited UnitKind &#123; Meters; Grams; kWh; Hours; &#125;</code></pre>
<p>The expression definitions are straightforward: literal expressions for
strings, booleans, and numbers, as well as generic literals and unit literals.</p>
<pre class="language-freon"><code class="language-freon">// TyperExample/src/defs/projectY.ast#L61-L75

expression BooleanLiteral base Exp &#123;
    xx: boolean;
&#125;

expression UnitLiteral base Exp &#123;
    // 62 kilogram, or 112 miles
    inner: NumberLiteral;
    unit: UnitKind;
&#125;

expression GenericLiteral base Exp &#123;
    // Set&#123; 12, 14, 16, 18 &#125;
    content: Exp[];
    kind: GenericKind;
&#125;</code></pre>
<SectionComponent tag="h2" 
  id="the-complete-ast-definition-2"
  bind:intersecting={visibleById["the-complete-ast-definition-2"]}
  onIntersectChange={(visible) => setVisible("the-complete-ast-definition-2", visible)}
>The Complete AST Definition</SectionComponent>
<pre class="language-freon"><code class="language-freon">// TyperExample/src/defs/projectY.ast

language projectY

model XX &#123;
    units: XXunit[];
&#125;

modelunit XXunit &#123;
    lines: ExpWithType[];
    file-extension = "expr";
&#125;

concept ExpWithType &#123;
    expr: Exp;
    type: TypeUsage;
&#125;

// definitions of types
abstract concept TypeUsage &#123; // to be used wherever a type can be used, either a referred type of or declared type is correct
&#125;

concept TypeRef base TypeUsage  &#123;
    reference type: NamedType;
&#125;

abstract concept TypeDecl base TypeUsage &#123;
&#125;

interface TopType &#123;
&#125;

concept NamedType implements TopType &#123;
    name: identifier;
&#125;

limited PredefinedType base NamedType &#123; NUMBER; BOOLEAN; STRING; ANY; NULL; &#125;

concept GenericType base TypeDecl &#123;
    baseType: TypeUsage;
    kind: GenericKind; // is it a set, sequence, bag, or anything else
&#125;
limited GenericKind &#123; Set; Sequence; Bag; Collection; &#125;

concept UnitOfMeasurement base TypeDecl &#123;
    reference baseType: PredefinedType; // is always NUMBER!!
    unit: UnitKind; // is it measured in km, kWh, grams, or anything else
&#125;
limited UnitKind &#123; Meters; Grams; kWh; Hours; &#125;

// definitions of expressions
abstract expression Exp &#123;
&#125;

expression NumberLiteral base Exp &#123;
    xx: number;
&#125;

expression StringLiteral base Exp &#123;
    xx: string;
&#125;

expression BooleanLiteral base Exp &#123;
    xx: boolean;
&#125;

expression UnitLiteral base Exp &#123;
    // 62 kilogram, or 112 miles
    inner: NumberLiteral;
    unit: UnitKind;
&#125;

expression GenericLiteral base Exp &#123;
    // Set&#123; 12, 14, 16, 18 &#125;
    content: Exp[];
    kind: GenericKind;
&#125;


expression NamedExp base Exp &#123;
    inner: Exp;
    myType: NamedType;
&#125;

expression PlusExp base Exp &#123;
    left: Exp;
    right: Exp;
&#125;
</code></pre>


<PrevNextSection {prevLink} {nextLink} />